const GA_MEASUREMENT_ID = "G-XH5ERV6TJW";
const CONSENT_STORAGE_KEY = "premierelens_consent";
const CONSENT_VERSION = 1;
export const OPEN_CONSENT_PREFERENCES_EVENT = "premierelens:open-consent-preferences";

const defaultConsent = {
  version: CONSENT_VERSION,
  analytics: false,
  marketing: false,
  updatedAt: null,
};

function safeParseConsent(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || parsed.version !== CONSENT_VERSION) {
      return null;
    }
    return {
      version: CONSENT_VERSION,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : null,
    };
  } catch {
    return null;
  }
}

export function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    return safeParseConsent(window.localStorage?.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return getStoredConsent()?.analytics === true;
}

export function buildConsentUpdate(consent = defaultConsent) {
  const analyticsGranted = consent.analytics === true ? "granted" : "denied";
  const marketingGranted = consent.marketing === true ? "granted" : "denied";
  return {
    analytics_storage: analyticsGranted,
    ad_storage: marketingGranted,
    ad_user_data: marketingGranted,
    ad_personalization: marketingGranted,
  };
}

const GA_SCRIPT_URL = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

function isGoogleAnalyticsLoaded() {
  return Boolean(document.querySelector(`script[src="${GA_SCRIPT_URL}"]`));
}

// Loads gtag.js once. Configuring sends the current page view, and Google's
// enhanced measurement then counts in-app (history) navigations.
function loadGoogleAnalytics() {
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
  const script = document.createElement("script");
  script.async = true;
  script.src = GA_SCRIPT_URL;
  document.head.appendChild(script);
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
}

export function applyConsentToGoogle(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", buildConsentUpdate(consent));
}

export function saveConsent(nextConsent) {
  if (typeof window === "undefined") return defaultConsent;
  const consent = {
    ...defaultConsent,
    ...nextConsent,
    version: CONSENT_VERSION,
    marketing: nextConsent?.marketing === true,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Consent still applies for the current page view even when storage is unavailable.
  }
  // Google's own switch: once set, loaded gtag.js sends nothing, not even cookieless pings.
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = !consent.analytics;
  if (!consent.analytics) {
    applyConsentToGoogle(consent);
  } else if (!isGoogleAnalyticsLoaded()) {
    ensureGtag();
    applyConsentToGoogle(consent);
    loadGoogleAnalytics();
  } else {
    // Allowed again after withdrawing in this visit: the script is already loaded.
    applyConsentToGoogle(consent);
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
  return consent;
}

export function initializeStoredConsent() {
  const consent = getStoredConsent();
  if (consent) {
    applyConsentToGoogle(consent);
  }
  return consent;
}

export function openConsentPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_CONSENT_PREFERENCES_EVENT));
}

export const trackEvent = (name, params = {}) => {
  if (
    typeof window !== "undefined"
    && typeof window.gtag === "function"
    && hasAnalyticsConsent()
  ) {
    window.gtag("event", name, params);
  }
};
